import {parsePhoneNumber, CountryCode} from 'libphonenumber-js';
import {asError} from './error';

/**
 * Errors that can occur when parsing a phone number.
 */
export enum PhoneNumberParseError {
  Empty = 'EMPTY_NUMBER',
  NotANumber = 'NOT_A_NUMBER',
  TooShort = 'TOO_SHORT',
  TooLong = 'TOO_LONG',
  Invalid = 'INVALID',
  Unknown = 'UNKNOWN_ERROR',
}

/**
 * Placeholder messages to present in lieu of a phone number parse error code.
 */
export type PhoneNumberErrorFallbacks = {
  [k in PhoneNumberParseError]: string;
};

/**
 * Default error placeholders for phone number parse errors.
 */
const defaultErrorFallbacks: PhoneNumberErrorFallbacks = {
  [PhoneNumberParseError.NotANumber]: 'not a number',
  [PhoneNumberParseError.TooShort]: 'too short',
  [PhoneNumberParseError.TooLong]: 'too long',
  [PhoneNumberParseError.Invalid]: 'not a real phone number',
  [PhoneNumberParseError.Empty]: 'missing number',
  [PhoneNumberParseError.Unknown]: 'unknown error',
};

/**
 * Format a phone number in the given locale.
 */
export const formatPhoneNumber = (
  n: string,
  locale?: CountryCode,
  fallbacks?: Partial<PhoneNumberErrorFallbacks>,
) => {
  const placeholders = {...defaultErrorFallbacks, ...fallbacks};
  try {
    if (!n) {
      throw new Error(PhoneNumberParseError.Empty);
    }

    const s = parsePhoneNumber(n, locale);
    if (!s.isValid()) {
      throw new Error(PhoneNumberParseError.Invalid);
    }

    return {
      text: s.formatNational(),
      valid: true,
      error: null,
    };
  } catch (e) {
    const errorType = asError(e).message as PhoneNumberParseError;
    if (Object.values(PhoneNumberParseError).includes(errorType)) {
      return {
        error: placeholders[errorType],
        valid: false,
        text: n,
      };
    } else {
      return {
        error: placeholders[PhoneNumberParseError.Unknown],
        valid: false,
        text: n,
      };
    }
  }
};
